import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Award } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { BadgeIcon, BADGE_ICONS } from "@/components/BadgeIcon";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum } from "@/lib/format";

const empty = { name: "", description: "", icon: "award", points: 0, active: true };

// Catálogo de insignias. Se entregan a mano desde Entregar a un cliente o desde la ficha del cliente.
export default function AdminBadges() {
  usePageTitle("Insignias · Puntos");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState<any>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<any>(null);

  const load = () => {
    setFailed(false);
    return api.get("/admin/badges").then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { setError("Escribe el nombre de la insignia."); return; }
    const pts = Number(form.points || 0);
    if (!Number.isInteger(pts) || pts < 0) { setError("Los puntos deben ser un número entero, 0 o más."); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description || null, icon: form.icon, points: pts, active: !!form.active };
      if (form.id) await api.put(`/admin/badges/${form.id}`, payload);
      else await api.post("/admin/badges", payload);
      toast.success("Insignia guardada");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/badges/${toDelete.id}`); toast.success("Insignia eliminada"); load(); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };

  if (failed) return <LoadError what="las insignias" onRetry={load} />;
  if (!data) return <PageLoader />;

  return (
    <div className="space-y-6" data-testid="admin-badges">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          Reconocimientos que el equipo entrega a mano, con su motivo. El cliente las ve en su portal. Se entregan desde <Link to="/admin/puntos" className="text-navy underline">Entregar a un cliente</Link> o desde la ficha del cliente.
        </p>
        <Button onClick={() => { setError(""); setForm({ ...empty }); }} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="new-badge">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nueva insignia
        </Button>
      </div>

      {data.badges.length === 0 ? (
        <EmptyState icon={Award}>Aún no hay insignias. Crea la primera con “Nueva insignia”.</EmptyState>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.badges.map((b: any) => (
            <li key={b.id} className={`card-obsidian p-5 ${b.active ? "" : "opacity-75"}`} data-testid="badge-card">
              <div className="flex items-start gap-3">
                <BadgeIcon icon={b.icon} muted={!b.active} />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900 flex items-baseline justify-between gap-2">{b.name}
                    {b.points > 0 && <span className="text-gold-ink font-mono font-semibold text-sm" data-testid="badge-points">+{fmtNum(b.points)} pts</span>}</p>
                  <p className="text-xs text-slate-600">{b.active ? "Activa" : "Inactiva"} · {fmtNum(b.awarded)} {b.awarded === 1 ? "cliente la tiene" : "clientes la tienen"}</p>
                </div>
              </div>
              {b.description && <p className="text-sm text-slate-600 mt-3">{b.description}</p>}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => { setError(""); setForm({ ...b }); }} className="flex-1 h-9 border-slate-200 text-slate-700"><Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar</Button>
                {b.awarded === 0 && (
                  <Button size="sm" variant="outline" onClick={() => setToDelete(b)} aria-label={`Eliminar ${b.name}`} className="h-9 border-red-500/30 text-red-700 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!form} onOpenChange={(o: boolean) => !o && !saving && setForm(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900" data-testid="badge-dialog">
          {form && <>
            <DialogHeader><DialogTitle className="font-serif text-xl">{form.id ? "Editar insignia" : "Nueva insignia"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="badge-name" className="text-sm text-slate-700">Nombre *</Label>
                <Input id="badge-name" data-testid="badge-name" maxLength={80} value={form.name} onChange={(e: any) => { setForm({ ...form, name: e.target.value }); setError(""); }} className="mt-1.5" placeholder="Ej. Embajador Armony" />
                {error && <p role="alert" className="text-xs text-red-700 mt-1">{error}</p>}
              </div>
              <div>
                <Label htmlFor="badge-desc" className="text-sm text-slate-700">Qué reconoce (lo ve el cliente)</Label>
                <Textarea id="badge-desc" rows={2} maxLength={300} value={form.description || ""} onChange={(e: any) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="badge-points" className="text-sm text-slate-700">Puntos que entrega</Label>
                <Input id="badge-points" data-testid="badge-points-input" type="number" min={0} value={form.points ?? 0}
                  onChange={(e: any) => { setForm({ ...form, points: e.target.value }); setError(""); }} className="mt-1.5 w-40" />
                <p className="text-xs text-slate-600 mt-1">Se suman al saldo del cliente al entregarle la insignia (0 = solo reconocimiento). Cambiarlos no afecta a quienes ya la recibieron.</p>
              </div>
              <fieldset>
                <legend className="text-sm text-slate-700 mb-1.5">Ícono</legend>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(BADGE_ICONS).map((k) => (
                    <label key={k} className={`p-1 rounded-full border-2 cursor-pointer ${form.icon === k ? "border-navy" : "border-transparent"}`}>
                      <input type="radio" name="badge-icon" value={k} checked={form.icon === k} onChange={() => setForm({ ...form, icon: k })} className="sr-only" aria-label={k} />
                      <BadgeIcon icon={k} size="sm" />
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="flex items-center gap-2">
                <Switch id="badge-active" checked={!!form.active} onCheckedChange={(v: boolean) => setForm({ ...form, active: v })} />
                <Label htmlFor="badge-active" className="text-sm text-slate-700">Activa (se puede entregar)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)} disabled={saving} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
              <Button onClick={save} disabled={saving} data-testid="badge-save" className="gold-gradient-bg text-white font-semibold rounded-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        danger
        title={`¿Eliminar la insignia “${toDelete?.name || ""}”?`}
        description={<p>Nadie la tiene todavía. Si ya la hubieras entregado, tendrías que desactivarla en lugar de eliminarla.</p>}
        confirmLabel="Eliminar insignia"
        onConfirm={remove}
      />
    </div>
  );
}
